/**
 * Admin → Plan rate overrides. Per-plan custom retail prices that win
 * at dispatch time over the rate-card cascade for specific (prefix,
 * message_type) shapes. See PR #94 of the billing alignment for the
 * data model and dispatcher integration.
 *
 * Backed by /v1/admin/plans/{id}/overrides/* — gated by
 * RequireReseller, which the parent /admin route group already
 * enforces. Customer-tenant users get 403.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface PlanOverride {
  id: number;
  plan_id: number;
  prefix: string;
  /** Empty = applies to any message_type (most permissive). */
  message_type?: string;
  sell_price: number;
  priority: number;
  /** RFC3339. The override only takes effect at/after this time. */
  effective_from: string;
  /** RFC3339. Empty = no upper bound (override stays active). */
  effective_to?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PlanOverridesResponse {
  items: PlanOverride[];
}

/**
 * Body for the upsert PUT. The shape key is (plan_id, prefix,
 * message_type, effective_from) — sending the same combination
 * updates the existing row instead of creating a duplicate.
 */
export interface PlanOverrideUpsertInput {
  prefix: string;
  message_type?: string;
  sell_price: number;
  priority?: number;
  /** RFC3339; empty means "now" (server defaults to NOW()). */
  effective_from?: string;
  /** RFC3339; empty means "open-ended". */
  effective_to?: string;
  is_active?: boolean;
  notes?: string;
}

const overridesKey = (planId: number) => ['admin', 'plans', planId, 'overrides'] as const;

export function usePlanOverrides(planId: number, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: overridesKey(planId),
    queryFn: () => api.get<PlanOverridesResponse>(`/v1/admin/plans/${planId}/overrides`),
    enabled: opts.enabled !== false && Number.isFinite(planId) && planId > 0,
    staleTime: 30_000,
  });
}

export function useUpsertPlanOverride(planId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlanOverrideUpsertInput) =>
      api.put<PlanOverride>(`/v1/admin/plans/${planId}/overrides`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: overridesKey(planId) });
    },
  });
}

export function useDeletePlanOverride(planId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (overrideId: number) =>
      api.delete<void>(`/v1/admin/plans/${planId}/overrides/${overrideId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: overridesKey(planId) });
    },
  });
}
